import { inject, Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, defer, EMPTY, Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, retry, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { Logger } from '@se/se.platform.base';
import { AuthenticationService } from '@se/platform';
import { IViewerVueClientSettings } from '../../../../../../../../Estensa.Web.Html5/.tmp/estensaModule';

export interface VuePhilipsMessage {
    type: VuePhilipsMessageType;
    param: string;
}

export type VuePhilipsMessageType =
    | 'login'
    | 'logout'
    | 'showstudy'
    | 'showstudywithpid'
    | 'showstudywithlastpresentation'
    | 'addimages'
    | 'closesession'
    | 'ping';

export interface VuePhilipsResponse {
    success: boolean;
    message?: string;
    data?: any;
}

export interface ShowStudyWithPidParams {
    pid: string;
    issuer?: string;
    accessionNumber: string;
    readingMode?: 0 | 1;
    remoteAeTitle?: string;
}

export interface ShowStudyWithLastPresentationParams {
    accessionNumber: string;
    pid: string;
    issuer?: string;
    readingMode?: 0 | 1;
    remoteAeTitle?: string;
}

export interface ShowStudyParams {
    accessionNumber: string;
    readingMode?: 0 | 1;
    remoteAeTitle?: string;
}

export interface LoginParams {
    username: string;
    password: string;
}

export enum VuePhilipsConnectionState {
    Disconnected = 'disconnected',
    Connecting = 'connecting',
    Connected = 'connected',
    Error = 'error',
}

@Injectable({
    providedIn: 'root',
})
export class VueClientPhilipsApiService {
    private DEFAULT_WS_URL = 'ws://localhost:43210/vuemotion';
    private DEFAULT_HTTP_URL = 'http://localhost:43210/vuemotion';
    private readonly RECONNECT_INTERVAL = 5000;
    private readonly MAX_RECONNECT_ATTEMPTS = 5;
    private readonly PING_INTERVAL = 30000;
    private readonly MESSAGE_TIMEOUT = 10000;

    private webSocket$: WebSocketSubject<VuePhilipsMessage | VuePhilipsResponse> | null = null;
    private destroy$ = new Subject<void>();
    private reconnectAttempts = 0;
    private pingInterval: ReturnType<typeof setInterval> | null = null;
    private connectionSubscription: any = null;
    private loggedIn = false;

    private connectionState$ = new BehaviorSubject<VuePhilipsConnectionState>(VuePhilipsConnectionState.Disconnected);
    private messages$ = new Subject<VuePhilipsResponse>();

    private authService = inject(AuthenticationService);
    private logger = inject(Logger);

    constructor() {}

    init(viewerSettings: IViewerVueClientSettings) {
        this.DEFAULT_WS_URL = `${viewerSettings.isHttps ? 'wss' : 'ws'}://${viewerSettings.serverName}:${viewerSettings.port}`;
        this.DEFAULT_HTTP_URL = `${viewerSettings.isHttps ? 'https' : 'http'}://${viewerSettings.serverName}:${viewerSettings.port}`;
    }
    hasCredentials = () => {
        const credentials = this.authService.getEncryptedCredentials();
        return credentials.length > 0;
    };
    /**
     * Get the current connection state as an observable
     */
    getConnectionState(): Observable<VuePhilipsConnectionState> {
        return this.connectionState$.asObservable();
    }

    /**
     * Get incoming messages from the viewer
     */
    getMessages(): Observable<VuePhilipsResponse> {
        return this.messages$.asObservable();
    }

    /**
     * Check if the service is currently connected
     */
    isConnected(): boolean {
        return this.connectionState$.value === VuePhilipsConnectionState.Connected;
    }

    /**
     * Check if currently logged into VueClientPhilips viewer
     */
    isLoggedIn(): boolean {
        return this.loggedIn;
    }

    /**
     * Connect to the VueClientPhilips Integration Engine via WebSocket using retry strategy
     */
    connect(wsUrl: string = this.DEFAULT_WS_URL): Observable<VuePhilipsConnectionState> {
        if (this.webSocket$ && !this.webSocket$.closed) {
            this.logger.debug('VueClientPhilips: Already connected');
            return this.connectionState$.asObservable();
        }

        this.connectionState$.next(VuePhilipsConnectionState.Connecting);
        this.logger.info('VueClientPhilips: Connecting to', wsUrl);

        // Clean up any existing subscription
        if (this.connectionSubscription) {
            this.connectionSubscription.unsubscribe();
        }

        // Reset reconnect attempts
        this.reconnectAttempts = 0;

        const connection$ = defer(() => this.createWebSocketConnection(wsUrl)).pipe(
            retry({
                count: this.MAX_RECONNECT_ATTEMPTS,
                delay: (error, retryCount) => {
                    this.reconnectAttempts = retryCount;
                    this.logger.warn(
                        `VueClientPhilips: Connection error, retry attempt ${retryCount}/${this.MAX_RECONNECT_ATTEMPTS}`,
                        this.getErrorMessage(error),
                    );
                    this.connectionState$.next(VuePhilipsConnectionState.Connecting);
                    this.logger.info(
                        `VueClientPhilips: Reconnecting in ${this.RECONNECT_INTERVAL}ms (attempt ${retryCount})`,
                    );
                    return timer(this.RECONNECT_INTERVAL);
                },
                resetOnSuccess: true,
            }),
            catchError((error) => {
                this.logger.error('VueClientPhilips: Connection failed permanently', this.getErrorMessage(error));
                this.connectionState$.next(VuePhilipsConnectionState.Error);
                this.stopPingInterval();
                return EMPTY;
            }),
            takeUntil(this.destroy$),
            finalize(() => {
                this.logger.debug('VueClientPhilips: Connection stream finalized');
                this.stopPingInterval();
            }),
        );

        this.connectionSubscription = connection$.subscribe({
            next: (message: VuePhilipsMessage | VuePhilipsResponse) => {
                if (this.isVueClientPhilipsResponse(message)) {
                    this.logger.debug('VueClientPhilips: Received message', JSON.stringify(message));
                    this.messages$.next(message);
                }
            },
            error: (error) => {
                this.logger.error('VueClientPhilips: Subscription error', this.getErrorMessage(error));
                this.connectionState$.next(VuePhilipsConnectionState.Error);
            },
        });

        return this.connectionState$.asObservable();
    }

    /**
     * Create WebSocket connection with lifecycle observers
     */
    private createWebSocketConnection(wsUrl: string): Observable<VuePhilipsMessage | VuePhilipsResponse> {
        return new Observable((observer) => {
            this.webSocket$ = webSocket<VuePhilipsMessage | VuePhilipsResponse>({
                url: wsUrl,
                openObserver: {
                    next: () => {
                        this.logger.info('VueClientPhilips: WebSocket connection opened');
                        this.connectionState$.next(VuePhilipsConnectionState.Connected);
                        this.reconnectAttempts = 0;
                        this.startPingInterval();
                    },
                },
                closeObserver: {
                    next: () => {
                        this.logger.warn('VueClientPhilips: WebSocket connection closed');
                        this.connectionState$.next(VuePhilipsConnectionState.Disconnected);
                        this.stopPingInterval();
                        observer.error(new Error('WebSocket connection closed'));
                    },
                },
            });

            const subscription = this.webSocket$.subscribe({
                next: (message) => observer.next(message),
                error: (error) => observer.error(error),
                complete: () => observer.complete(),
            });

            return () => {
                subscription.unsubscribe();
                if (this.webSocket$ && !this.webSocket$.closed) {
                    this.webSocket$.complete();
                }
            };
        });
    }

    /**
     * Disconnect from the VueClientPhilips Integration Engine
     */
    disconnect(): void {
        this.logger.info('VueClientPhilips: Disconnecting');
        this.stopPingInterval();
        this.loggedIn = false;

        if (this.connectionSubscription) {
            this.connectionSubscription.unsubscribe();
            this.connectionSubscription = null;
        }

        if (this.webSocket$ && !this.webSocket$.closed) {
            this.webSocket$.complete();
            this.webSocket$ = null;
        }

        this.connectionState$.next(VuePhilipsConnectionState.Disconnected);
    }

    /**
     * Start ping interval to keep connection alive
     */
    private startPingInterval(): void {
        this.stopPingInterval();
        this.pingInterval = setInterval(() => {
            if (this.isConnected()) {
                this.ping().subscribe({
                    error: (error) => {
                        this.logger.error('VueClientPhilips: Ping failed', this.getErrorMessage(error));
                    },
                });
            }
        }, this.PING_INTERVAL);
    }

    /**
     * Stop ping interval
     */
    private stopPingInterval(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    /**
     * Send ping message to keep connection alive
     */
    private ping(): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'ping',
            param: '',
        });
    }

    /**
     * Send a message to the VueClientPhilips Integration Engine
     */
    private sendMessage(message: VuePhilipsMessage): Observable<VuePhilipsResponse> {
        if (!this.webSocket$ || this.webSocket$.closed) {
            return throwError(() => new Error('WebSocket is not connected'));
        }

        return new Observable<VuePhilipsResponse>((observer) => {
            const subscription = this.messages$
                .pipe(
                    filter((response) => response !== null),
                    take(1),
                    timeout(this.MESSAGE_TIMEOUT),
                    catchError((error) => {
                        this.logger.error('VueClientPhilips: Message timeout or error', this.getErrorMessage(error));
                        return throwError(() => error);
                    }),
                )
                .subscribe({
                    next: (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error) => observer.error(error),
                });

            try {
                this.webSocket$!.next(message);
                this.logger.debug('VueClientPhilips: Sent message', JSON.stringify(message));
            } catch (error) {
                observer.error(error);
            }

            return () => subscription.unsubscribe();
        });
    }

    /**
     * Type guard to check if message is a response
     */
    private isVueClientPhilipsResponse(message: VuePhilipsMessage | VuePhilipsResponse): message is VuePhilipsResponse {
        return 'success' in message;
    }

    /**
     * Get error message from error object
     */
    private getErrorMessage(error: any): string {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === 'string') {
            return error;
        }
        return JSON.stringify(error);
    }

    /**
     * Perform login into the VueClient
     */
    login(): Observable<VuePhilipsResponse> {
        const credentials = this.authService.getEncryptedCredentials();
        return this.sendMessage({
            type: 'login',
            param: credentials, //`${params.username};${params.password}`,
        }).pipe(
            tap((response) => {
                if (response.success) {
                    this.loggedIn = true;
                }
            }),
        );
    }

    /**
     * Perform logout from the VueClient
     */
    logout(): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'logout',
            param: '',
        }).pipe(
            tap(() => {
                this.loggedIn = false;
            }),
        );
    }

    /**
     * Show study in VueClient
     */
    showStudy(params: ShowStudyParams): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'showstudy',
            param: `${params.accessionNumber}${params.readingMode ? `;${params.readingMode}` : ''}${
                params.remoteAeTitle ? `;${params.remoteAeTitle}` : ''
            }`,
        });
    }

    /**
     * Show study with PID in VueClient
     */
    showStudyWithPid(params: ShowStudyWithPidParams): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'showstudywithpid',
            param: `${params.pid}${params.issuer ? `;${params.issuer}` : ''};${params.accessionNumber}${
                params.readingMode ? `;${params.readingMode}` : ''
            }${params.remoteAeTitle ? `;${params.remoteAeTitle}` : ''}`,
        });
    }

    /**
     * Show study with last presentation in VueClient
     */
    showStudyWithLastPresentation(params: ShowStudyWithLastPresentationParams): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'showstudywithlastpresentation',
            param: `${params.accessionNumber};${params.pid}${params.issuer ? `;${params.issuer}` : ''}${
                params.readingMode ? `;${params.readingMode}` : ''
            }${params.remoteAeTitle ? `;${params.remoteAeTitle}` : ''}`,
        });
    }

    /**
     * Add images to the VueClient
     */
    addImages(imagePaths: string[]): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'addimages',
            param: imagePaths.join('|'),
        });
    }

    /**
     * Close session in VueClient
     */
    closeSession(): Observable<VuePhilipsResponse> {
        return this.sendMessage({
            type: 'closesession',
            param: '',
        });
    }

    /**
     * Perform login via HTTP URL call
     */
    loginViaUrl(params: LoginParams, baseUrl: string = this.DEFAULT_HTTP_URL): string {
        return `${baseUrl}/?action=login&token=${encodeURIComponent(params.username)};${encodeURIComponent(params.password)}`;
    }

    /**
     * Perform logout via HTTP URL call
     */
    logoutViaUrl(baseUrl: string = this.DEFAULT_HTTP_URL): string {
        return `${baseUrl}/?action=logout`;
    }

    /**
     * Show study with PID via HTTP URL call
     */
    showStudyWithPidViaUrl(params: ShowStudyWithPidParams, baseUrl: string = this.DEFAULT_HTTP_URL): string {
        let url = `${baseUrl}/?action=showstudywithpid&pid=${encodeURIComponent(params.pid)}`;

        if (params.issuer) {
            url += `&issuer=${encodeURIComponent(params.issuer)}`;
        }

        url += `&accnum=${encodeURIComponent(params.accessionNumber)}`;

        if (params.readingMode !== undefined) {
            url += `&readingMode=${params.readingMode}`;
        }

        return url;
    }

    /**
     * Show study with last presentation via HTTP URL call
     */
    showStudyWithLastPresentationViaUrl(
        params: ShowStudyWithLastPresentationParams,
        baseUrl: string = this.DEFAULT_HTTP_URL,
    ): string {
        let url = `${baseUrl}/?action=showstudywithlastpresentation&accnum=${encodeURIComponent(params.accessionNumber)}`;
        url += `&pid=${encodeURIComponent(params.pid)}`;

        if (params.issuer) {
            url += `&issuer=${encodeURIComponent(params.issuer)}`;
        }

        if (params.readingMode !== undefined) {
            url += `&readingMode=${params.readingMode}`;
        }

        return url;
    }
}
